/**
 * Auth Repository Implementation (Infrastructure)
 *
 * Implements the domain repository interface.
 * Coordinates between datasource and token storage.
 *
 * Note: User-related operations are in the user module.
 */

import { AppException, ErrorCode } from '@/core/exceptions';

import type { AuthRepository } from '../../domain/repositories';
import type {
  LoginRequest,
  LogoutRequest,
} from '../../domain/types/requests';
import type {
  AuthResponse,
  TokenRefreshResponse,
  GoogleAuthUrlResponse,
} from '../../domain/types/responses';
import type { AuthDatasource } from '../datasources';
import type { TokenStorage } from '../services';
import { authDatasource } from '../datasources';
import { tokenStorage } from '../services';

// ============================================================================
// Repository Implementation
// ============================================================================

export class AuthRepositoryImpl implements AuthRepository {
  constructor(
    private readonly datasource: AuthDatasource = authDatasource,
    private readonly storage: TokenStorage = tokenStorage
  ) {}

  async getGoogleAuthUrl(): Promise<GoogleAuthUrlResponse> {
    try {
      return await this.datasource.getGoogleAuthUrl();
    } catch (error) {
      throw this.handleError(error, 'Failed to get Google auth URL');
    }
  }

  async googleLoginWithAuthCode(authCode: string, scopes?: string): Promise<AuthResponse> {
    try {
      const response = await this.datasource.googleLoginWithAuthCode(authCode, scopes);

      // Store tokens and user data
      this.storage.setTokens(response.tokens);
      this.storage.setUserData(response.user);

      return response;
    } catch (error) {
      throw this.handleError(error, 'Google login failed');
    }
  }

  async login(request: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await this.datasource.login(request);

      // Store tokens and user data
      this.storage.setTokens(response.tokens);
      this.storage.setUserData(response.user);

      return response;
    } catch (error) {
      throw this.handleError(error, 'Login failed');
    }
  }

  async refreshToken(): Promise<TokenRefreshResponse> {
    try {
      const response = await this.datasource.refreshToken();

      // Update stored tokens
      this.storage.setTokens(response.tokens);

      return response;
    } catch (error) {
      // On refresh failure, clear all auth data
      this.storage.clearAll();
      throw this.handleError(error, 'Token refresh failed');
    }
  }

  async logout(request?: LogoutRequest): Promise<void> {
    try {
      await this.datasource.logout(request);
    } catch {
      // Ignore logout API errors - we still want to clear local state
    } finally {
      // Always clear local auth state
      this.storage.clearAll();
    }
  }

  /**
   * Transform errors to domain exceptions
   */
  private handleError(error: unknown, fallbackMessage: string): AppException {
    if (error instanceof AppException) {
      return error;
    }

    const message = error instanceof Error ? error.message : fallbackMessage;

    return new AppException({
      code: ErrorCode.AUTHENTICATION_FAILED,
      message,
      userMessage: message,
    });
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const authRepository: AuthRepository = new AuthRepositoryImpl();
